import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Role } from '@agencia/shared';
import { api, ApiError } from '@/lib/api';

export type AuthStage = 'MFA_SETUP' | 'MFA_VERIFY' | 'CHANGE_PASSWORD' | 'OK';

export interface SessionUser {
  id: string;
  nome: string;
  email: string;
  role: Role;
  /** Ligado a um vendedor do Conta Azul. */
  vendedor: boolean;
}

type State =
  | { status: 'loading' }
  | { status: 'setup' } // nenhum usuário existe: primeiro acesso
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: SessionUser; stage: AuthStage };

interface AuthContextValue {
  state: State;
  /** Recarrega quem está logado e em que etapa (após login, 2FA, troca de senha...). */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<{ user: SessionUser; stage: AuthStage }>('/auth/me');
      setState({ status: 'authenticated', user: me.user, stage: me.stage });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        const { needsSetup } = await api.get<{ needsSetup: boolean }>('/setup/status');
        setState({ status: needsSetup ? 'setup' : 'anonymous' });
        return;
      }
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void refresh().catch(() => setState({ status: 'anonymous' }));
  }, [refresh]);

  useEffect(() => {
    const onExpired = () => setState({ status: 'anonymous' });
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const value = useMemo(() => ({ state, refresh, logout }), [state, refresh, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}

/** Usuário logado com acesso completo. Use apenas dentro das telas protegidas. */
export function useCurrentUser(): SessionUser {
  const { state } = useAuth();
  if (state.status !== 'authenticated') throw new Error('Tela protegida renderizada sem login');
  return state.user;
}
